import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Save, Check, AlertCircle, X, Image as Star, Plus, Edit, Trash2 } from 'lucide-react';
import { validateStep, isStepComplete, canValidateShop } from '../utils/shopValidation';

const ShopStepEditor = ({ shop, onSave, onCancel, apiUrl, token, categories }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [editedShop, setEditedShop] = useState({ ...shop });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const steps = [
    {
      id: 'essential',
      title: 'Informations Importantes',
      subtitle: 'Les données essentielles de la boutique',
      required: true,
      fields: ['name', 'categories', 'description', 'mainPhone', 'logo']
    },
    {
      id: 'complementary',
      title: 'Informations Complémentaires',
      subtitle: 'Détails additionnels (optionnel)',
      required: false,
      fields: ['product_type', 'mainAddress']
    },
    {
      id: 'avis',
      title: 'Avis Clients',
      subtitle: 'Gérez les avis et notes de la boutique',
      required: false,
      fields: ['avis']
    },
    {
      id: 'seo',
      title: 'SEO & Informations légales',
      subtitle: 'Optimisez le référencement et ajoutez les infos légales',
      required: false,
      fields: ['meta_title', 'meta_keywords', 'rcs']
    }
  ];

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        if (!value || value.trim().length === 0) return 'Le nom est obligatoire';
        if (value.length < 3) return 'Le nom doit contenir au moins 3 caractères';
        return null;
      case 'description':
        if (!value || value.trim().length === 0) return 'La description est obligatoire';
        if (value.length < 20) return 'La description doit contenir au moins 20 caractères';
        if (value.length > 150) return 'La description ne peut pas dépasser 150 caractères';
        return null;
      case 'categories':
        if (!value || value.length === 0) return 'Au moins une catégorie est obligatoire';
        return null;
      case 'mainPhone':
        if (!value || value.trim().length === 0) return 'Le téléphone principal est obligatoire';
        return null;
      case 'logo':
        if (!value || value.trim().length === 0) return 'Le logo est obligatoire';
        return null;
      case 'mainAddress':
        if (!value || value.trim().length === 0) return 'L\'adresse principale est obligatoire';
        return null;
      default:
        return null;
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedShop({ ...editedShop, [field]: value });
    
    const error = validateField(field, value);
    if (error) {
      setErrors({ ...errors, [field]: error });
    } else {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleSave = async (validate = false) => {
    if (validate && !canValidateShop()) {
      alert('❌ Tous les champs obligatoires doivent être remplis avant de valider');
      return;
    }

    setSaving(true);
    try {
      const shopToSave = {
        ...editedShop,
        status: validate ? 'validated' : editedShop.status
      };

      await onSave(shopToSave);
      
      if (validate) {
        alert('✅ Boutique validée et enregistrée !');
      } else {
        alert('✅ Modifications enregistrées');
      }
    } catch (error) {
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  const currentStepData = steps[currentStep];
  const stepValidationErrors = validateStep(editedShop, currentStepData.id);

  return (
    <div className="card" style={{ maxHeight: '90vh', overflow: 'auto' }}>
      <div className="card-header">
        <h2>Édition de {shop.name || shop.domain}</h2>
      </div>
      
      <div className="details-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '5px' }}>
            {steps.map((step, index) => {
              const stepComplete = isStepComplete(editedShop, step.id);
              const stepErrors = validateStep(editedShop, step.id);
              const hasErrors = stepErrors.length > 0;
              
              return (
                <div
                  key={step.id}
                  onClick={() => goToStep(index)}
                  style={{
                    flex: '1 1 80px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    padding: '10px 5px',
                    position: 'relative',
                    minWidth: '80px'
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      margin: '0 auto 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      backgroundColor: index === currentStep 
                        ? '#3b82f6' 
                        : stepComplete
                        ? '#10b981'
                        : hasErrors
                        ? '#ef4444'
                        : '#e5e7eb',
                      color: index === currentStep || stepComplete || hasErrors ? 'white' : '#6b7280',
                      transition: 'all 0.3s',
                      border: hasErrors ? '2px solid #dc2626' : 'none'
                    }}
                  >
                    {stepComplete ? <Check size={20} /> : hasErrors ? <AlertCircle size={20} /> : index + 1}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: index === currentStep ? '600' : '400',
                    color: index === currentStep ? '#3b82f6' : hasErrors ? '#ef4444' : '#6b7280'
                  }}>
                    {step.title}
                    {step.required && <span style={{ color: '#ef4444' }}> *</span>}
                  </div>
                  {hasErrors && (
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#ef4444', 
                      marginTop: '4px',
                      fontWeight: '600'
                    }}>
                      {stepErrors.length} erreur{stepErrors.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ 
            height: '4px', 
            backgroundColor: '#e5e7eb', 
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              height: '100%', 
              width: `${((currentStep + 1) / steps.length) * 100}%`,
              backgroundColor: '#3b82f6',
              transition: 'width 0.3s'
            }} />
          </div>
        </div>

        {/* Step Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: stepValidationErrors.length > 0 ? '#fee2e2' : '#f9fafb',
          borderRadius: '12px',
          border: stepValidationErrors.length > 0 ? '2px solid #ef4444' : 'none'
        }}>
          <h3 style={{ 
            fontSize: '24px', 
            color: '#111827', 
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            {currentStepData.title}
            {currentStepData.required && (
              <span style={{ color: '#ef4444', fontSize: '14px' }}>(obligatoire)</span>
            )}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            {currentStepData.subtitle}
          </p>
        </div>

        {/* Step Content */}
        <div style={{ minHeight: '400px' }}>
          {currentStep === 0 && (
            <EssentialInfoStep 
              shop={editedShop}
              categories={categories}
              onChange={handleFieldChange}
              errors={errors}
            />
          )}
          
          {currentStep === 1 && (
            <ComplementaryInfoStep 
              shop={editedShop}
              onChange={handleFieldChange}
              errors={errors}
            />
          )}
          
          {currentStep === 2 && (
            <AvisStep 
              shop={editedShop}
              apiUrl={apiUrl}
              token={token}
            />
          )}
          
          {currentStep === 3 && (
            <SEOStep 
              shop={editedShop}
              onChange={handleFieldChange}
              errors={errors}
            />
          )}
        </div>

        {/* Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginTop: '30px',
          paddingTop: '20px',
          borderTop: '2px solid #e5e7eb',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className="btn btn-secondary"
            style={{ flex: '1 1 100px' }}
          >
            <ChevronLeft className="icon" />
            Précédent
          </button>

          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="btn btn-primary"
            style={{ flex: '1 1 150px' }}
          >
            <Save className="icon" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={nextStep}
              className="btn btn-primary"
              style={{ flex: '1 1 100px' }}
            >
              Suivant
              <ChevronRight className="icon" />
            </button>
          ) : (
            <button
              onClick={() => handleSave(true)}
              disabled={!canValidateShop(editedShop) || saving}
              className="btn btn-success"
              style={{ flex: '1 1 150px' }}
              title={!canValidateShop(editedShop) ? 'Tous les champs obligatoires doivent être remplis' : ''}
            >
              <Check className="icon" />
              Valider la boutique
            </button>
          )}

          <button
            onClick={onCancel}
            className="btn btn-secondary"
          >
            <X className="icon" />
            Annuler
          </button>
        </div>

        {/* Status messages */}
        {!canValidateShop(editedShop) && (
          <div style={{ 
            marginTop: '15px', 
            padding: '12px', 
            backgroundColor: '#fef3c7', 
            borderRadius: '8px',
            fontSize: '13px',
            color: '#92400e'
          }}>
            <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
            Complétez tous les champs obligatoires pour valider la boutique
          </div>
        )}
      </div>
    </div>
  );
};

// Step 1: Essential Info
const EssentialInfoStep = ({ shop, categories, onChange, errors }) => (
  <div className="form-section">
    <div className="form-group" style={{ 
      backgroundColor: errors.name ? '#fee2e2' : 'transparent',
      padding: errors.name ? '12px' : '0',
      borderRadius: errors.name ? '8px' : '0',
      border: errors.name ? '2px solid #ef4444' : 'none'
    }}>
      <label>
        Nom de la boutique *
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Le nom commercial ou l'enseigne)
        </span>
      </label>
      <input
        type="text"
        value={shop.name || ''}
        onChange={(e) => onChange('name', e.target.value)}
        placeholder="Ex: Darty, Boulanger, FNAC..."
        className="inline-edit-input"
        style={{ borderColor: errors.name ? '#ef4444' : undefined }}
      />
      {errors.name && (
        <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
          ⚠️ {errors.name}
        </span>
      )}
    </div>

    <div className="form-group" style={{ 
      backgroundColor: errors.categories ? '#fee2e2' : 'transparent',
      padding: errors.categories ? '12px' : '0',
      borderRadius: errors.categories ? '8px' : '0',
      border: errors.categories ? '2px solid #ef4444' : 'none'
    }}>
      <label>
        Catégories principales *
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Maintenez Ctrl/Cmd pour sélectionner plusieurs)
        </span>
      </label>
      <select
        multiple
        value={shop.categories || []}
        onChange={(e) => {
          const selected = Array.from(e.target.selectedOptions, option => option.value);
          onChange('categories', selected);
        }}
        className="inline-edit-select-multiple"
        size="8"
        style={{ borderColor: errors.categories ? '#ef4444' : undefined }}
      >
        {categories.map(cat => (
          <option key={cat._id} value={cat._id}>
            {cat.name}
          </option>
        ))}
      </select>
      {errors.categories && (
        <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
          ⚠️ {errors.categories}
        </span>
      )}
      {shop.categories && shop.categories.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <div className="categories-display">
            {shop.categories.map((catId, idx) => {
              const cat = categories.find(c => c._id === catId);
              return cat ? (
                <span key={idx} className="category-badge" style={{ backgroundColor: cat.color }}>
                  {cat.name}
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>

    <div className="form-group" style={{ 
      backgroundColor: errors.description ? '#fee2e2' : 'transparent',
      padding: errors.description ? '12px' : '0',
      borderRadius: errors.description ? '8px' : '0',
      border: errors.description ? '2px solid #ef4444' : 'none'
    }}>
      <label>
        Description *
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (20-150 caractères - Décrivez brièvement les services de la boutique)
        </span>
      </label>
      <textarea
        value={shop.description || ''}
        onChange={(e) => onChange('description', e.target.value)}
        placeholder="Ex: Spécialiste en électroménager et high-tech avec service après-vente de qualité..."
        className="inline-edit-textarea"
        rows="4"
        maxLength="150"
        style={{ borderColor: errors.description ? '#ef4444' : undefined }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        <span style={{ color: errors.description ? '#ef4444' : '#6b7280', fontSize: '12px', fontWeight: errors.description ? '600' : 'normal' }}>
          {errors.description ? `⚠️ ${errors.description}` : ''}
        </span>
        <span className="char-count">
          {(shop.description || '').length}/150
        </span>
      </div>
    </div>

    <div className="form-group" style={{ 
      backgroundColor: errors.mainPhone ? '#fee2e2' : 'transparent',
      padding: errors.mainPhone ? '12px' : '0',
      borderRadius: errors.mainPhone ? '8px' : '0',
      border: errors.mainPhone ? '2px solid #ef4444' : 'none'
    }}>
      <label>
        Téléphone principal (SAV) *
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Numéro du service après-vente)
        </span>
      </label>
      <input
        type="text"
        value={shop.mainPhone || ''}
        onChange={(e) => onChange('mainPhone', e.target.value)}
        placeholder="Ex: 01 23 45 67 89"
        className="inline-edit-input"
        style={{ borderColor: errors.mainPhone ? '#ef4444' : undefined }}
      />
      {errors.mainPhone && (
        <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
          ⚠️ {errors.mainPhone}
        </span>
      )}
    </div>

    <div className="form-group" style={{ 
      backgroundColor: errors.logo ? '#fee2e2' : 'transparent',
      padding: errors.logo ? '12px' : '0',
      borderRadius: errors.logo ? '8px' : '0',
      border: errors.logo ? '2px solid #ef4444' : 'none'
    }}>
      <label>
        Logo (URL) *
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Lien vers l'image du logo)
        </span>
      </label>
      <input
        type="text"
        value={shop.logo || ''}
        onChange={(e) => onChange('logo', e.target.value)}
        placeholder="https://example.com/logo.png"
        className="inline-edit-input"
        style={{ borderColor: errors.logo ? '#ef4444' : undefined }}
      />
      {errors.logo && (
        <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
          ⚠️ {errors.logo}
        </span>
      )}
      {shop.logo && (
        <div style={{ 
          marginTop: '15px', 
          padding: '15px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <label style={{ display: 'block', marginBottom: '10px', fontSize: '12px', color: '#6b7280' }}>
            Aperçu du logo
          </label>
          <img 
            src={shop.logo} 
            alt="Logo" 
            style={{ 
              maxWidth: '200px', 
              maxHeight: '100px',
              objectFit: 'contain'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div style={{ display: 'none', color: '#ef4444', fontSize: '12px', marginTop: '8px' }}>
            ❌ Impossible de charger l'image
          </div>
        </div>
      )}
    </div>

    <div style={{ 
      padding: '16px', 
      backgroundColor: '#eff6ff', 
      borderRadius: '8px',
      marginTop: '20px'
    }}>
      <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
        💡 <strong>Conseil :</strong> Ces informations sont essentielles pour la fiche de la boutique. Vérifiez qu'elles sont correctes avant de continuer.
      </p>
    </div>
  </div>
);

// Step 2: Complementary Info
const ComplementaryInfoStep = ({ shop, onChange, errors }) => (
  <div className="form-section">
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#eff6ff', 
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
        📋 Ces informations sont optionnelles mais permettent d'enrichir la fiche de la boutique
      </p>
    </div>

    <div className="form-group">
      <label>
        Type de produit
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Précisez la spécialité de la boutique)
        </span>
      </label>
      <input
        type="text"
        value={shop.product_type || ''}
        onChange={(e) => onChange('product_type', e.target.value)}
        placeholder="Ex: Électronique grand public, Électroménager..."
        className="inline-edit-input"
      />
    </div>

    <div className="form-group" style={{ 
      backgroundColor: errors.mainAddress ? '#fee2e2' : 'transparent',
      padding: errors.mainAddress ? '12px' : '0',
      borderRadius: errors.mainAddress ? '8px' : '0',
      border: errors.mainAddress ? '2px solid #ef4444' : 'none'
    }}>
      <label>
        Adresse principale *
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Adresse du siège ou du service client)
        </span>
      </label>
      <input
        type="text"
        value={shop.mainAddress || ''}
        onChange={(e) => onChange('mainAddress', e.target.value)}
        placeholder="Ex: 123 Rue de la République, 75001 Paris"
        className="inline-edit-input"
        style={{ borderColor: errors.mainAddress ? '#ef4444' : undefined }}
      />
      {errors.mainAddress && (
        <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
          ⚠️ {errors.mainAddress}
        </span>
      )}
    </div>

    {(shop.product_type || shop.mainAddress) && (
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#f0fdf4', 
        borderRadius: '8px',
        marginTop: '20px',
        border: '1px solid #10b981'
      }}>
        <p style={{ fontSize: '13px', color: '#065f46', margin: 0, fontWeight: '600', marginBottom: '10px' }}>
          ✅ Informations renseignées :
        </p>
        {shop.product_type && (
          <p style={{ fontSize: '13px', color: '#047857', margin: '5px 0' }}>
            • Type de produit : {shop.product_type}
          </p>
        )}
        {shop.mainAddress && (
          <p style={{ fontSize: '13px', color: '#047857', margin: '5px 0' }}>
            • Adresse : {shop.mainAddress}
          </p>
        )}
      </div>
    )}
  </div>
);

// Step 3: Avis
const AvisStep = ({ shop, apiUrl, token }) => {
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [formData, setFormData] = useState({ note: 5, content: '', firstname: '', lastname: '' });

  const loadAvis = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/shops/${shop._id}/avis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvis(data.avis || []);
    } catch (error) {
      console.error('Error loading avis:', error);
      setAvis([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvis();
  }, []);

  const handleAddAvis = async () => {
    if (!formData.content.trim() || !formData.firstname.trim() || !formData.lastname.trim()) {
      alert('Tous les champs sont obligatoires');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/shops/${shop._id}/avis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('✅ Avis ajouté avec succès');
        setFormData({ note: 5, content: '', firstname: '', lastname: '' });
        setAddingNew(false);
        loadAvis();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding avis:', error);
      alert('❌ Erreur lors de l\'ajout');
    }
  };

  const handleUpdateAvis = async (index) => {
    if (!formData.content.trim() || !formData.firstname.trim() || !formData.lastname.trim()) {
      alert('Tous les champs sont obligatoires');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/shops/${shop._id}/avis/${index}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('✅ Avis modifié avec succès');
        setEditingIndex(null);
        setFormData({ note: 5, content: '', firstname: '', lastname: '' });
        loadAvis();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error updating avis:', error);
      alert('❌ Erreur lors de la modification');
    }
  };

  const handleDeleteAvis = async (index) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/shops/${shop._id}/avis/${index}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Avis supprimé avec succès');
        loadAvis();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting avis:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const startEdit = (av, index) => {
    setEditingIndex(index);
    setFormData({ ...av });
    setAddingNew(false);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setAddingNew(false);
    setFormData({ note: 5, content: '', firstname: '', lastname: '' });
  };

  const calculateAverage = () => {
    if (avis.length === 0) return 0;
    const sum = avis.reduce((acc, av) => acc + av.note, 0);
    return (sum / avis.length).toFixed(1);
  };

  if (loading) {
    return <div className="form-section">Chargement des avis...</div>;
  }

  return (
    <div className="form-section">
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#eff6ff', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
          ⭐ Gérez les avis clients de la boutique. Note moyenne: <strong>{calculateAverage()}/5</strong> sur {avis.length} avis
        </p>
      </div>

      {/* Bouton ajouter */}
      {!addingNew && editingIndex === null && (
        <button
          onClick={() => setAddingNew(true)}
          className="btn btn-primary"
          style={{ marginBottom: '20px', width: '100%' }}
        >
          <Plus className="icon" />
          Ajouter un avis
        </button>
      )}

      {/* Formulaire d'ajout/édition */}
      {(addingNew || editingIndex !== null) && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '8px',
          marginBottom: '20px',
          border: '2px solid #3b82f6'
        }}>
          <h4 style={{ marginTop: 0, color: '#111827' }}>
            {addingNew ? '➕ Nouvel avis' : '✏️ Modifier l\'avis'}
          </h4>
          
          <div className="form-group">
            <label>Note *</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  size={32}
                  style={{ cursor: 'pointer' }}
                  className={n <= formData.note ? 'star-filled' : 'star-empty'}
                  onClick={() => setFormData({ ...formData, note: n })}
                />
              ))}
              <span style={{ marginLeft: '10px', fontSize: '18px', fontWeight: '600' }}>
                {formData.note}/5
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>Prénom *</label>
            <input
              type="text"
              value={formData.firstname}
              onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
              placeholder="Ex: Jean"
              className="inline-edit-input"
            />
          </div>

          <div className="form-group">
            <label>Nom *</label>
            <input
              type="text"
              value={formData.lastname}
              onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
              placeholder="Ex: Dupont"
              className="inline-edit-input"
            />
          </div>

          <div className="form-group">
            <label>Commentaire *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Décrivez votre expérience..."
              className="inline-edit-textarea"
              rows="4"
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <button
              onClick={() => addingNew ? handleAddAvis() : handleUpdateAvis(editingIndex)}
              className="btn btn-success"
              style={{ flex: 1 }}
            >
              <Check className="icon" />
              {addingNew ? 'Ajouter' : 'Enregistrer'}
            </button>
            <button
              onClick={cancelEdit}
              className="btn btn-secondary"
            >
              <X className="icon" />
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Liste des avis */}
      {avis.length > 0 ? (
        <div className="avis-list">
          {avis.map((av, idx) => (
            <div key={idx} className="avis-item" style={{ 
              border: editingIndex === idx ? '2px solid #3b82f6' : '1px solid #e5e7eb',
              position: 'relative'
            }}>
              <div className="avis-header">
                <div className="avis-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      size={14} 
                      className={i < av.note ? 'star-filled' : 'star-empty'}
                    />
                  ))}
                </div>
                <span className="avis-author">{av.firstname} {av.lastname}</span>
              </div>
              <p className="avis-content">{av.content}</p>
              
              {editingIndex !== idx && (
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '10px',
                  paddingTop: '10px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={() => startEdit(av, idx)}
                    className="btn btn-primary"
                    style={{ flex: 1, fontSize: '12px', padding: '6px' }}
                  >
                    <Edit size={14} className="icon" />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDeleteAvis(idx)}
                    className="btn btn-danger"
                    style={{ fontSize: '12px', padding: '6px' }}
                  >
                    <Trash2 size={14} className="icon" />
                    Supprimer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#6b7280',
          backgroundColor: '#f9fafb',
          borderRadius: '8px'
        }}>
          <p>Aucun avis pour le moment</p>
        </div>
      )}
    </div>
  );
};

// Step 4: SEO
const SEOStep = ({ shop, onChange, errors }) => (
  <div className="form-section">
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#eff6ff', 
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
        🔍 Optimisez le référencement et ajoutez les informations légales
      </p>
    </div>

    <div className="form-group">
      <label>
        Meta Title
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Titre optimisé pour les moteurs de recherche)
        </span>
      </label>
      <input
        type="text"
        value={shop.meta_title || ''}
        onChange={(e) => onChange('meta_title', e.target.value)}
        placeholder="Ex: Darty SAV - Service Client et Réparation"
        className="inline-edit-input"
        maxLength="60"
      />
      <span className="char-count">
        {(shop.meta_title || '').length}/60
      </span>
    </div>

    <div className="form-group">
      <label>
        Meta Keywords
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Mots-clés séparés par des virgules)
        </span>
      </label>
      <input
        type="text"
        value={(shop.meta_keywords || []).join(', ')}
        onChange={(e) => onChange('meta_keywords', e.target.value.split(',').map(k => k.trim()).filter(k => k))}
        placeholder="sav, réparation, service client, garantie"
        className="inline-edit-input"
      />
      <span className="form-help">
        Exemple : sav, service client, réparation, garantie
      </span>
    </div>

    <div className="form-group">
      <label>
        RCS (Numéro d'immatriculation)
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
          (Registre du Commerce et des Sociétés)
        </span>
      </label>
      <input
        type="text"
        value={shop.rcs || ''}
        onChange={(e) => onChange('rcs', e.target.value)}
        placeholder="Ex: RCS Paris 123 456 789"
        className="inline-edit-input"
      />
    </div>

    <div style={{ 
      padding: '16px', 
      backgroundColor: '#f9fafb', 
      borderRadius: '8px',
      marginTop: '20px'
    }}>
      <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
        💡 <strong>Astuce SEO :</strong> Un bon meta title contient le nom de la marque et les mots-clés principaux (SAV, service client).
      </p>
    </div>
  </div>
);

export default ShopStepEditor